import { useState } from "react";
import { useForm } from "react-hook-form";
import { House } from "lucide-react";
import * as accountApi from "../../../api/account/api";
import { useApiError } from "../hooks/useApiError";
import { BackToLoginLink } from "../components/BackToLoginLink";
import { PageLoader } from "../components/PageLoader";
import { ErrorAlert } from "../components/ErrorAlert";
import { SubmitButton } from "../components/SubmitButton";
import { LoginFormField } from "../components/LoginFormField";
import { MicrosoftIcon } from "../../../components/logos/MicrosoftIcon";
import { LoginViewState } from "../types";
import { getBaseUtcOffset } from "../utils/timezone";
import { FormOverlay } from "@/components/common/Loading";

type Props = {
  customerMode: boolean;
  onViewChange: (view: LoginViewState) => void;
};

type AccountFormValues = {
  account: string;
};

export const AccountSelectionView = ({ customerMode, onViewChange }: Props) => {
  const { apiError, setApiError, clearError } = useApiError();
  const [redirecting, setRedirecting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AccountFormValues>();

  const onSubmit = async (data: AccountFormValues) => {
    clearError();
    try {
      const { authUrl } = await accountApi.ssoInitiate({
        account: data.account.trim(),
        customerMode,
        timezone: getBaseUtcOffset(),
      });
      setRedirecting(true);
      window.location.href = authUrl;
    } catch {
      setApiError("Unable to initiate Microsoft sign-in. Please try again.");
    }
  };

  return (
    <>
      {redirecting && <PageLoader />}

      <FormOverlay loading={isSubmitting || redirecting}>

      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-[#E5E7EB] bg-white shrink-0">
          <MicrosoftIcon />
        </div>
        <div>
          <h2 className="text-[clamp(1.5rem,2vw,2.25rem)] font-extrabold text-[#111827] m-0 leading-tight">
            Sign in with Microsoft
          </h2>
        </div>
      </div>

      <p className="text-[clamp(0.875rem,1vw,1.1rem)] text-[#6B7280] mb-7 leading-[1.6]">
        Enter your Klipboard CRM account name to continue with Microsoft 365 sign-in.
      </p>

      {apiError && <ErrorAlert message={apiError} />}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LoginFormField
          label="Account"
          placeholder="Your account name"
          autoComplete="organization"
          error={errors.account?.message}
          disabled={isSubmitting}
          icon={<House size={16} color="#9CA3AF" strokeWidth={2} />}
          {...register("account", { required: "Account is required" })}
        />

        <div className="mb-3">
          <SubmitButton loading={isSubmitting}>Continue with Microsoft 365</SubmitButton>
        </div>
      </form>

      <BackToLoginLink onClick={() => onViewChange("login")} />
      </FormOverlay>
    </>
  );
};
